import { z } from 'zod';
import { fetchJson } from './api';
import { queryToSearchParams } from './api-utils';
import { type Network, Token, type Activity, Contract } from './interfaces';

export function formatNetwork(network: Network): string {
  if (network === 'mainnet') {
    return 'Ethereum';
  }

  return network.charAt(0).toUpperCase() + network.slice(1);
}

export async function getMultichainContract(
  fetch: typeof window.fetch,
  coinType: string,
  chainId: string,
  contractAddress: string,
) {
  const url = `/api/v2/contracts/${coinType}/${chainId}/${contractAddress}`;

  const { contract } = await fetchJson(fetch, url);

  return Contract.parse(contract);
}

export async function getMultichainToken(
  fetch: typeof window.fetch,
  coinType: string,
  chainId: string,
  contractAddress: string,
  tokenId: string,
  forceRefresh: boolean,
): Promise<Token | undefined> {
  let url = `/api/v2/tokens/${coinType}/${chainId}/${contractAddress}/${tokenId}`;
  if (forceRefresh) {
    url += '?refresh=true';
  }
  const { token } = await fetchJson(fetch, url);

  return Token.parse(token);
}

export async function getMultichainActivity(
  fetch: typeof window.fetch,
  transactionHash: string,
  query: {
    coinType?: string;
    chainId: string;
    address?: string;
  },
): Promise<Activity | undefined> {
  const json = await fetchJson(
    fetch,
    `/api/v2/activities/transaction/${transactionHash}?${queryToSearchParams(query)}`,
  );

  const events = json.activity.events;

  return {
    timestamp: json.activity.timestamp,
    coinType: json.activity.coinType,
    chainId: json.activity.chainId,
    transactionHash: json.activity.transaction.hash,
    events,
  };
}

export async function getMultichainBalances(
  fetch: typeof window.fetch,
  query: {
    addresses?: string[] | string;
    address?: string;
    coinType?: number;
    offset?: number;
    limit?: number;
    chainIds?: string[] | string;
  },
): Promise<Token[]> {
  const searchParams = queryToSearchParams({
    ...query,
    nfts: true,
    blockSpam: true,
  });

  const json = await fetchJson(fetch, `/api/v2/balances?${searchParams.toString()}`);

  return z.array(Token).parse(json.balances);
}
